/**
 * The crosshair, the cooldown ring, and the line that says what a shot did.
 *
 * DOM only; every decision it renders is made by the pure helpers in `tag.ts`.
 * Same split as `RoundHud` beside it, and for the same reason — the question
 * "what should this say" is worth testing without a browser.
 *
 * The crosshair element already existed as a plain dot in `index.html`. It
 * grows a state here because a hunter needs to know three things at a glance
 * that a dot cannot say: whether they can shoot at all, whether the last shot
 * cost them the next few seconds, and how much of that is left.
 */

import {
  TagOutcome,
  type RoundPhaseId,
  type TagMsg,
  type TeamId,
} from "@prop-hunt/shared";
import {
  CrosshairState,
  cooldownProgress,
  cooldownSeconds,
  cooldownTotalFor,
  crosshairState,
  tagMessage,
  type CrosshairStateId,
} from "./tag.js";

/** How long a shot's result stays on screen. */
const MESSAGE_HOLD_MS = 2200;

export interface TagHudStats {
  phase: RoundPhaseId;
  myTeam: TeamId;
  amFound: boolean;
  /** Ticks until this client may shoot again, by its own reckoning. */
  cooldownTicksLeft: number;
  /** Ticks the current cooldown started from, for the ring's fill. */
  cooldownTotalTicks: number;
}

export class TagHud {
  private readonly dot: HTMLElement;
  private readonly ring: HTMLElement;
  private readonly messageEl: HTMLElement;

  /** Identity of the tag currently displayed, so the same one is not replayed. */
  private shownTag: TagMsg | null = null;
  private messageUntil = 0;

  constructor(crosshair: HTMLElement, root: HTMLElement) {
    crosshair.innerHTML = "";
    crosshair.classList.add("crosshair");

    this.ring = document.createElement("div");
    this.ring.className = "crosshair-ring";
    this.dot = document.createElement("div");
    this.dot.className = "crosshair-dot";
    crosshair.append(this.ring, this.dot);

    this.messageEl = document.createElement("div");
    this.messageEl.className = "tag-message";
    this.messageEl.hidden = true;
    root.appendChild(this.messageEl);
  }

  /**
   * Show a shot's result, if it is one we have not already shown.
   *
   * Compared by identity rather than by value: `Connection.lastTag` holds the
   * newest message and is not cleared, so this is called with the same object
   * every frame until another shot happens. Two identical shots — a hunter
   * missing the same wall twice — are different objects and both display.
   */
  onTag(tag: TagMsg | null, localId: number, nameOf: (id: number) => string, now: number): void {
    if (tag === null || tag === this.shownTag) return;
    this.shownTag = tag;

    const text = tagMessage(tag, localId, nameOf);
    if (text === null) return;
    this.messageEl.textContent = text;
    this.messageEl.dataset.outcome =
      tag.outcome === TagOutcome.Found ? "found"
      : tag.outcome === TagOutcome.Missed ? "missed"
      : "refused";
    this.messageUntil = now + MESSAGE_HOLD_MS;
  }

  update(stats: TagHudStats, now: number): void {
    const state = crosshairState(
      stats.phase, stats.myTeam, stats.amFound, stats.cooldownTicksLeft,
    );
    this.applyState(state, stats);
    this.messageEl.hidden = now > this.messageUntil;
  }

  private applyState(state: CrosshairStateId, stats: TagHudStats): void {
    this.dot.dataset.state = state;
    this.ring.hidden = state !== CrosshairState.Cooling;
    this.dot.hidden = state === CrosshairState.Hidden;

    if (state !== CrosshairState.Cooling) return;
    const progress = cooldownProgress(stats.cooldownTicksLeft, stats.cooldownTotalTicks);
    // A conic gradient rather than an SVG arc: one custom property, no layout,
    // and it animates for free because it is repainted every frame anyway.
    this.ring.style.setProperty("--progress", `${progress * 360}deg`);
    // Only worth a number when the wait is long enough to be worth waiting out.
    const seconds = cooldownSeconds(stats.cooldownTicksLeft);
    this.ring.dataset.seconds = seconds > 1 ? String(seconds) : "";
  }
}

/**
 * Track the cooldown from the shooter's own `Msg.Tag`.
 *
 * Cosmetic, and structurally unable to be anything else: the client cannot
 * evaluate a shot, so it cannot know a cooldown started until the server tells
 * it. That makes this copy run slightly long — it starts when the message
 * lands rather than when the server resolved the shot — which is the safe
 * direction. A crosshair that reads ready while the server would still refuse
 * is the version that feels broken.
 */
export class CooldownTracker {
  private readyAtTick = 0;
  private totalTicks = 0;
  private seenTag: TagMsg | null = null;

  onTag(tag: TagMsg | null, localId: number, serverTick: number): void {
    if (tag === null || tag === this.seenTag) return;
    this.seenTag = tag;
    if (tag.shooterId !== localId) return;

    const total = cooldownTotalFor(tag.outcome);
    // A refused shot must not extend the cooldown that refused it, or holding
    // the button would make the wait permanent.
    if (total <= 0) return;
    this.totalTicks = total;
    this.readyAtTick = serverTick + total;
  }

  ticksLeft(serverTick: number): number {
    const left = this.readyAtTick - serverTick;
    return left > 0 ? left : 0;
  }

  get total(): number {
    return this.totalTicks;
  }
}
